import CampaignGanttChart from '@/components/CampaignGanttChart'
import TableTabButton from '@/components/TableTabButton'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import AddCircle from '@mui/icons-material/AddCircle'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import CampaignDetails from '../components/CampaignDetails'
import usePermissions from '@/hooks/usePermissions'

const mapStateToProps = ({ campaign, loading }) => ({
  campaignList: campaign.campaignList,
  campaignsStatusTotal: campaign.campaignsStatusTotal,
  isLoading: loading['campaign/getCampaigns'],
  isDetailsLoading: loading['campaign/getCampaignDetails'],
})

const fieldMap = {
  trainingCampName: 'CAMP_NAME',
  trainingCampStart: 'CAMP_START',
  labels: 'CAMP_LABEL',
  frequence: 'CAMP_REQ',
  initialTime: 'CAMP_START',
  endTime: 'CAMP_END',
  remainDays: 'CAMP_REMAIN',
  status: 'CAMP_STATUS',
}

const CampaignDashboard = ({
  dispatch,
  campaignList,
  campaignsStatusTotal,
  isLoading,
  isDetailsLoading,
}) => {
  const { data, paging = {} } = campaignList || {}

  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { campaignId } = params
  const [filterDateRange, setFilterDateRange] = useState()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState([{ field: 'trainingCampStart', sort: 'desc' }])
  const [showChart, setShowChart] = useState(true)
  const [isCustomRange, setIsCustomRange] = useState(true)
  const [selectedDateOpt, setSelectedDateOpt] = useState(6)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const form = useRef()
  const detailsRef = useRef()

  const canCreate = usePermissions('courseTrainingEvent_create')

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const cols = [
    {
      field: 'trainingCampName',
      headerName: t('general.campaign_name'),
      width: 150,
      renderCell: ({ value }) => (
        <Tooltip title={value} placement="top-end">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'labels',
      headerName: t('general.tag'),
      width: 150,
      valueFormatter: ({ value = [] }) => value.join(','),
    },
    { field: 'frequence', headerName: t('general.play_num'), width: 120 },
    {
      field: 'initialTime',
      headerName: t('general.start_time'),
      width: 200,
      valueFormatter: ({ value }) => moment(value).format(DATE_TIME_FORMAT),
    },
    {
      field: 'endTime',
      headerName: t('general.end_time'),
      width: 200,
      valueFormatter: ({ value }) => moment(value).format(DATE_TIME_FORMAT),
    },
    { field: 'remainDays', headerName: t('general.remaining_days'), width: 130 },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 150,
      valueFormatter: ({ value }) => t(`status.${value}`),
    },
  ]

  const resetPage = () => {
    setPage(0)
  }

  const fetchData = useCallback(() => {
    if (filterDateRange) {
      const payload = {
        searchCampInitialTime: moment(filterDateRange.startDate).toDate(),
        searchCampEndTime: moment(filterDateRange.endDate).toDate(),
        searchCampName: search,
        sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
        pageIndex: page,
        pageSize,
      }

      dispatch({
        type: 'campaign/getCampaigns',
        payload,
      })

      dispatch({
        type: 'campaign/getCampaignsStatusTotal',
        payload,
      })
    }
  }, [dispatch, filterDateRange, search, sort, page, pageSize])

  const handleRowClick = d => {
    navigate(`/campaign/${d.trainingCampId}`)
    fetchData()
  }

  const fetchCampaignDetails = useCallback(() => {
    if (campaignId) {
      dispatch({
        type: 'campaign/getCampaignDetails',
        payload: { campaignId },
        successCallback: () => {
          setTimeout(() => {
            detailsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
          }, 200)
        },
      })
    }
  }, [dispatch, campaignId])

  useEffect(() => {
    fetchData()
  }, [sort, page, filterDateRange, search, pageSize, fetchData])

  useEffect(() => {
    resetPage()
  }, [filterDateRange, search])

  useEffect(() => {
    if (selectedDateOpt !== '0') {
      const monthValue = Math.floor(selectedDateOpt / 2)
      const remainder = (selectedDateOpt / 2) % 1
      const dayValue = 30 * remainder

      const startDate = moment().subtract(monthValue, 'month').subtract(dayValue, 'day').toDate()
      const endDate = moment().add(monthValue, 'month').add(dayValue, 'day').toDate()

      form.current?.setValue?.('dateRangeFilter', {
        startDate,
        endDate,
      })

      setFilterDateRange({
        startDate,
        endDate,
      })

      setIsCustomRange(false)
    } else {
      setIsCustomRange(true)
    }
  }, [selectedDateOpt])

  useEffect(() => {
    if (campaignId) {
      fetchCampaignDetails()
      setTimeout(() => {
        detailsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
      }, 200)
    }
  }, [campaignId, fetchCampaignDetails])

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item md={10}>
                  <FormCreator
                    ref={form}
                    sx={{ width: '80%' }}
                    formItems={[
                      ...(canCreate
                        ? [
                            {
                              component: () => (
                                <Button
                                  variant="text"
                                  color="secondary_80"
                                  endIcon={<AddCircle />}
                                  onClick={() => {
                                    navigate('/campaign_create')
                                  }}
                                >
                                  {t('general.add_campaign')}
                                </Button>
                              ),
                            },
                          ]
                        : []),
                      {
                        label: t('general.custom_date_range'),
                        name: 'dateRangeFilter',
                        type: 'dateRange',
                        disabled: !isCustomRange,
                        defaultValue: { startDate: new Date(), endDate: new Date() },
                        inputConfig: {
                          onChange: value => {
                            setFilterDateRange(value)
                          },
                        },
                      },
                      {
                        label: t('general.quick_search'),
                        name: 'search',
                        defaultValue: '',
                        inputConfig: {
                          onSubmit: value => {
                            setSearch(value)
                          },
                        },
                      },
                      {
                        label: t('general.quick_filter'),
                        name: 'setDateRange',
                        type: 'radio',
                        defaultValue: 6,
                        inputConfig: {
                          onChange: _ => {
                            setSelectedDateOpt(_.target.value)
                          },
                          options: [
                            { value: 1, label: t('general.month_num', { num: 1 }) },
                            { value: 2, label: t('general.month_num', { num: 2 }) },
                            { value: 3, label: t('general.month_num', { num: 3 }) },
                            { value: 6, label: t('general.month_num', { num: 6 }) },
                            { value: 0, label: t('general.custom') },
                          ],
                        },
                      },
                    ]}
                    flexDirection="row"
                    submitComponent={() => null}
                    resetComponent={() => null}
                  />
                </Grid>
                <Grid item md={2}>
                  <Stack direction="row">
                    <Box flex={1} />
                    <TableTabButton
                      selected={showChart}
                      onClick={() => {
                        setShowChart(true)
                      }}
                      label={t('general.bar_chart')}
                    />
                    <TableTabButton
                      selected={!showChart}
                      onClick={() => {
                        setShowChart(false)
                      }}
                      label={t('general.table')}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
            {showChart ? (
              <CampaignGanttChart
                loading={isLoading}
                height={400}
                data={data || []}
                statusTotal={campaignsStatusTotal}
                nameKey="trainingCampName"
                startDateKey="initialTime"
                endDateKey="endTime"
                onRowClick={handleRowClick}
                pagination={pagination}
                startDate={filterDateRange?.startDate}
                endDate={filterDateRange?.endDate}
              />
            ) : (
              <Table
                minHeight={400}
                columns={cols}
                rows={data || []}
                dataGridConfig={{
                  sortingMode: 'server',
                  sortingOrder: ['desc', 'asc', null],
                  sortModel: sort,
                  onSortModelChange: newSort => setSort(newSort),
                  onRowClick: d => {
                    handleRowClick(d.row)
                  },
                  getRowId: row => row.trainingCampId,
                  loading: isLoading,
                }}
                noRowsLabel={t('errors.no_campaign')}
                pagination={pagination}
              />
            )}
          </Card>
        </Grid>

        <Grid ref={detailsRef} item>
          {isDetailsLoading && (
            <Box
              sx={{ padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CircularProgress />
            </Box>
          )}
          {!isDetailsLoading && campaignId && (
            <Card>
              <CardContent>
                <CampaignDetails />
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  )
}

export default connect(mapStateToProps)(CampaignDashboard)
